import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MIN_USER_SEARCH_LENGTH, searchDirectoryUsers } from '../api/directory';
import type { DirectoryUser } from '../api/types';
import { ErrorBanner } from './ErrorBanner';

/** The picked user. `directoryUser` is present when it came from a search rather than a typed object id. */
export interface UserSelection {
  userId: string;
  displayName: string | null;
  directoryUser?: DirectoryUser;
}

export interface UserPickerProps {
  selection: UserSelection | null;
  onChange: (selection: UserSelection | null) => void;
  disabled?: boolean;
  /**
   * Which directory endpoint to search. Defaults to the member-management one, which is restricted to group
   * admins; the contact-card page passes its own, open to any group member.
   */
  search?: (term: string, take: number, signal?: AbortSignal) => Promise<DirectoryUser[]>;
  /**
   * Whether to offer typing an object id when the search cannot be used. Right when the id is what the form
   * needs; pointless when the search is only a way to pre-fill fields the user can type themselves.
   */
  allowManualId?: boolean;
  /** One line beside other controls: the labels stay for screen readers, the search hint only while searching. */
  compact?: boolean;
}

const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 10;

function subtitle(user: DirectoryUser): string | null {
  const address = user.mail ?? user.userPrincipalName;
  return address && address !== user.displayName ? address : null;
}

/**
 * Searches Microsoft Entra ID as the admin types and hands back the picked user's object id. If the search
 * cannot be used - the Graph permission is not consented, or the lookup is switched off - the error is shown
 * and entering an object id by hand is offered, so the form is never a dead end.
 */
export function UserPicker({
  selection,
  onChange,
  disabled = false,
  search = searchDirectoryUsers,
  allowManualId = true,
  compact = false,
}: UserPickerProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualId, setManualId] = useState('');

  useEffect(() => {
    const trimmed = term.trim();
    if (selection !== null || trimmed.length < MIN_USER_SEARCH_LENGTH) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setSearching(true);
      search(trimmed, RESULT_LIMIT, controller.signal)
        .then((users) => {
          setResults(users);
          setError(null);
        })
        .catch((caught: unknown) => {
          // An aborted request is a superseded keystroke, not a failure.
          if (!controller.signal.aborted) {
            setResults([]);
            setError(caught);
            setManualMode(allowManualId);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setSearching(false);
          }
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // `search` belongs here, so it has to be a stable reference - both callers pass a module-level function.
  }, [term, selection, search, allowManualId]);

  function select(user: DirectoryUser) {
    onChange({ userId: user.id, displayName: user.displayName, directoryUser: user });
    setResults([]);
    setTerm('');
  }

  function clear() {
    onChange(null);
    setTerm('');
    setManualId('');
  }

  if (selection !== null) {
    return (
      <div className="field">
        <span className={compact ? 'visually-hidden' : 'form-label'} id="picked-user-label">
          <i className="pi pi-user me-1" aria-hidden="true" />
          {t('userPicker.pickedUser')}
        </span>
        <div className={compact ? 'picked-user picked-user--compact' : 'picked-user'}>
          <i className="pi pi-user-plus" aria-hidden="true" />
          <span id="picked-user">
            {selection.displayName ?? t('userPicker.unnamed')}{' '}
            <span className="mono">({selection.userId})</span>
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={clear}
            disabled={disabled}
          >
            <i className="pi pi-refresh me-1" aria-hidden="true" />
            {t('userPicker.change')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="field">
        <label htmlFor="user-search" className={compact ? 'visually-hidden' : undefined}>
          <i className="pi pi-search me-1" aria-hidden="true" />
          {t('userPicker.searchLabel')}
        </label>
        <input
          id="user-search"
          type="search"
          className="form-control"
          value={term}
          autoComplete="off"
          placeholder={t('userPicker.searchPlaceholder')}
          disabled={disabled}
          onChange={(event) => setTerm(event.target.value)}
        />
        <span className={compact && !searching ? 'visually-hidden' : 'hint'}>
          {searching ? (
            <>
              <i className="pi pi-spin pi-spinner me-1" aria-hidden="true" />
              {t('userPicker.searching')}
            </>
          ) : (
            t('userPicker.hint', { count: MIN_USER_SEARCH_LENGTH })
          )}
        </span>
      </div>

      <ErrorBanner error={error} />

      {results.length > 0 && (
        <ul className="user-results">
          {results.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={() => select(user)}
                disabled={disabled}
              >
                <i className="pi pi-user me-1" aria-hidden="true" />
                {user.displayName}
              </button>
              {subtitle(user) && <span className="hint"> {subtitle(user)}</span>}
            </li>
          ))}
        </ul>
      )}

      {!searching && error === null && term.trim().length >= MIN_USER_SEARCH_LENGTH && results.length === 0 && (
        <p className="hint">{t('userPicker.noMatch', { term: term.trim() })}</p>
      )}

      {!allowManualId ? null : manualMode ? (
        <div className="field">
          <label htmlFor="manual-oid">
            <i className="pi pi-key me-1" aria-hidden="true" />
            {t('userPicker.manualIdLabel')}
          </label>
          <input
            id="manual-oid"
            type="text"
            className="form-control"
            value={manualId}
            placeholder="00000000-0000-0000-0000-000000000000"
            disabled={disabled}
            onChange={(event) => setManualId(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-light mt-2 align-self-start"
            disabled={disabled || manualId.trim() === ''}
            onClick={() => onChange({ userId: manualId.trim(), displayName: null })}
          >
            <i className="pi pi-check me-2" aria-hidden="true" />
            {t('userPicker.useThisId')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-link btn-sm ps-0"
          onClick={() => setManualMode(true)}
          disabled={disabled}
        >
          <i className="pi pi-key me-1" aria-hidden="true" />
          {t('userPicker.enterIdInstead')}
        </button>
      )}
    </>
  );
}
